import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Modal, Label, Input, Button } from "reactstrap";
import MetaTags from "react-meta-tags";
import Datatables from "pages/table/datatable";
import { tableColumns } from "./components/TableColumns";
import { transactions } from "AllDummyData/DummyData";
import handleExportCSV from "pages/utils/handleExportCSV";
import FilterBar from "./components/FilterBar";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";
import { get, post } from "helpers/api_helper";

const Transactions = () => {
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [viewingTransaction, setViewingTransaction] = useState(null);
    const [transactionsList, setTransactionsList] = useState([]);
    const [refundAmount, setRefundAmount] = useState("");
    const [refundReason, setRefundReason] = useState("");
    const [disputeReason, setDisputeReason] = useState("");
    const [showRefundForm, setShowRefundForm] = useState(false);
    const [showDisputeForm, setShowDisputeForm] = useState(false);

    const fetchTransactions = useCallback(async () => {
        try {
            const res = await get("/superadmin/transactions");
            const list = res?.data || res;
            if (Array.isArray(list) && list.length > 0) {
                const formatted = list.map((t, idx) => {
                    let customer = { name: "—", email: "—", phone: "—" };
                    try { if (t.customer_details) customer = typeof t.customer_details === "string" ? JSON.parse(t.customer_details) : t.customer_details; } catch { }

                    let refundsList = [];
                    try { if (t.refunds) refundsList = typeof t.refunds === "string" ? JSON.parse(t.refunds) : t.refunds; } catch { }

                    let disputesList = [];
                    try { if (t.disputes) disputesList = typeof t.disputes === "string" ? JSON.parse(t.disputes) : t.disputes; } catch { }

                    let webhookHistoryList = [];
                    try { if (t.webhook_history) webhookHistoryList = typeof t.webhook_history === "string" ? JSON.parse(t.webhook_history) : t.webhook_history; } catch { }

                    let auditTrailList = [];
                    try { if (t.audit_trail) auditTrailList = typeof t.audit_trail === "string" ? JSON.parse(t.audit_trail) : t.audit_trail; } catch { }

                    return {
                        id: t.transaction_id || idx + 1,
                        entity: t.Entity?.entity_title || customer?.name || "Company #" + (t.entity_id || idx + 1),
                        email: t.Entity?.contact_email || customer?.email || "—",
                        payment_gateway: t.payment_gateway || "Stripe",
                        payment_reference_id: t.payment_reference_id || "tx_" + Math.random().toString(36).substr(2, 9),
                        amount: t.amount || 0,
                        currency: t.currency || "USD",
                        payment_mode: t.payment_mode || "Card",
                        status: t.status === 1 ? "Approved" : t.status === 2 ? "Rejected" : "Pending",
                        created_at: t.created_at ? new Date(t.created_at).toLocaleDateString() : new Date().toLocaleDateString(),
                        fees: t.fees || 0,
                        customer_details: customer,
                        refunds: refundsList,
                        disputes: disputesList,
                        webhook_history: webhookHistoryList,
                        audit_trail: auditTrailList,
                        raw: t
                    };
                });
                setTransactionsList(formatted);

                // Keep modal refreshed if open
                if (viewingTransaction) {
                    const refreshed = formatted.find(x => x.id === viewingTransaction.id);
                    if (refreshed) {
                        setViewingTransaction(refreshed);
                    }
                }
            }
        } catch (e) {
            console.warn("Using fallback transactions data", e);
        }
    }, [viewingTransaction]);

    useEffect(() => {
        fetchTransactions();
    }, []);

    const handleAction = (type, row) => {
        if (type === "View") {
            setViewingTransaction(row);
            setShowRefundForm(false);
            setShowDisputeForm(false);
        }
    };

    const toggleModal = () => {
        setViewingTransaction(null);
    };

    const handleRefundSubmit = async () => {
        if (!refundAmount || isNaN(refundAmount) || Number(refundAmount) <= 0) {
            showErrorAlert("Please enter a valid refund amount.");
            return;
        }
        try {
            await post(`/superadmin/transactions/${viewingTransaction.id}/refund`, {
                amount: Number(refundAmount),
                reason: refundReason
            });
            showSuccessAlert("Refund processed successfully!");
            setRefundAmount("");
            setRefundReason("");
            setShowRefundForm(false);
            fetchTransactions();
        } catch (e) {
            showErrorAlert("Failed to process refund.");
        }
    };

    const handleDisputeSubmit = async () => {
        try {
            await post(`/superadmin/transactions/${viewingTransaction.id}/dispute`, {
                reason: disputeReason
            });
            showSuccessAlert("Mock chargeback dispute created!");
            setDisputeReason("");
            setShowDisputeForm(false);
            fetchTransactions();
        } catch (e) {
            showErrorAlert("Failed to open dispute.");
        }
    };

    return (
        <React.Fragment>
            <div className="dash-page ms-4 me-4">
                <MetaTags>
                    <title>Transactions | {process.env.REACT_APP_TITLE}</title>
                </MetaTags>

                <Container fluid>
                    <div className="p-3">
                        <Row className="align-items-center mb-4">
                            <Col md="4">
                                <h4 className="mb-0 fw-bold text-dark">Transactions History</h4>
                                <p className="text-muted mb-0">Monitor fee structures, disputes, and complete gateway lifecycle audits</p>
                            </Col>
                            <Col md="8" className="d-flex justify-content-end gap-2">
                                <button className="btn btn-success border px-3" onClick={handleExportCSV}>
                                    <i className="bx bx-export me-1"></i> Export All ({selectedRowIds.length})
                                </button>
                            </Col>
                        </Row>

                        <FilterBar />

                        <div className="p-3 bg-white shadow-sm border" style={{ borderRadius: "10px" }}>
                            <Datatables
                                columns={tableColumns(handleAction)}
                                showTableOnly={true}
                                rows={transactionsList}
                                keyField={"id"}
                                isCheckbox={true}
                                loading={false}
                                showPagination={true}
                                setSelectedRowIds={setSelectedRowIds}
                            />
                        </div>
                    </div>

                    {/* Detailed Lifecycle Audit Modal */}
                    <Modal isOpen={!!viewingTransaction} toggle={toggleModal} size="xl" className="custom-modal">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">Transaction Lifecycle Audit — #{viewingTransaction?.payment_reference_id}</h5>
                            <button type="button" onClick={toggleModal} className="close border-0 bg-transparent" aria-label="Close">
                                <i className="bx bx-x h3 m-0" />
                            </button>
                        </div>

                        <div className="modal-body p-4">
                            {viewingTransaction && (
                                <Row className="g-4">
                                    {/* Left Column: Details & Financials */}
                                    <Col lg="7">
                                        <div className="border rounded p-3 mb-4 bg-light">
                                            <h6 className="fw-bold mb-3 text-primary"><i className="bx bx-receipt me-1" /> Payment Parameters</h6>
                                            <Row className="g-3">
                                                <Col md="6">
                                                    <span className="small text-muted d-block">Client Name</span>
                                                    <span className="fw-semibold text-dark">{viewingTransaction.entity}</span>
                                                </Col>
                                                <Col md="6">
                                                    <span className="small text-muted d-block">Email Address</span>
                                                    <span className="fw-semibold text-dark">{viewingTransaction.email}</span>
                                                </Col>
                                                <Col md="6">
                                                    <span className="small text-muted d-block">Customer Name</span>
                                                    <span className="fw-semibold text-dark">{viewingTransaction.customer_details?.name || "—"}</span>
                                                </Col>
                                                <Col md="6">
                                                    <span className="small text-muted d-block">Customer Phone</span>
                                                    <span className="fw-semibold text-dark">{viewingTransaction.customer_details?.phone || "—"}</span>
                                                </Col>
                                                <Col md="6">
                                                    <span className="small text-muted d-block">Payment Gateway</span>
                                                    <span className="fw-semibold text-dark">{viewingTransaction.payment_gateway}</span>
                                                </Col>
                                                <Col md="6">
                                                    <span className="small text-muted d-block">Payment Mode</span>
                                                    <span className="fw-semibold text-dark">{viewingTransaction.payment_mode}</span>
                                                </Col>
                                            </Row>
                                        </div>

                                        <div className="border rounded p-3 mb-4 bg-light">
                                            <h6 className="fw-bold mb-3 text-success"><i className="bx bx-money-withdraw me-1" /> Financial Breakdown</h6>
                                            <Row className="g-3 text-center">
                                                <Col xs="4">
                                                    <span className="small text-muted d-block">Gross Amount</span>
                                                    <h5 className="fw-bold text-dark mt-1">{viewingTransaction.amount} {viewingTransaction.currency}</h5>
                                                </Col>
                                                <Col xs="4">
                                                    <span className="small text-muted d-block">Gateway Fees</span>
                                                    <h5 className="fw-bold text-danger mt-1">-{viewingTransaction.fees} {viewingTransaction.currency}</h5>
                                                </Col>
                                                <Col xs="4">
                                                    <span className="small text-muted d-block">Net Settlement</span>
                                                    <h5 className="fw-bold text-success mt-1">
                                                        {(viewingTransaction.amount - viewingTransaction.fees).toFixed(2)} {viewingTransaction.currency}
                                                    </h5>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Actions Panel */}
                                        <div className="border rounded p-3 bg-light">
                                            <h6 className="fw-bold mb-3 text-dark"><i className="bx bx-shield-quarter me-1" /> Resolution Actions</h6>
                                            <div className="d-flex gap-2 mb-3">
                                                {viewingTransaction.status === "Approved" && (
                                                    <>
                                                        <Button color="primary" className="fw-semibold px-4" onClick={() => { setShowRefundForm(!showRefundForm); setShowDisputeForm(false); }}>
                                                            <i className="bx bx-redo me-1" /> Issue Refund
                                                        </Button>
                                                        <Button color="danger" className="fw-semibold px-4" onClick={() => { setShowDisputeForm(!showDisputeForm); setShowRefundForm(false); }}>
                                                            <i className="bx bx-error-circle me-1" /> Log Chargeback Dispute
                                                        </Button>
                                                    </>
                                                )}
                                                {viewingTransaction.status === "Rejected" && (
                                                    <span className="text-muted small">This transaction was rejected/disputed. No further actions can be taken.</span>
                                                )}
                                            </div>

                                            {showRefundForm && (
                                                <div className="border rounded p-3 bg-white mb-2 animate__animated animate__fadeIn">
                                                    <h6 className="fw-bold text-dark mb-3">Process Refund Payment</h6>
                                                    <Row className="g-2 mb-2">
                                                        <Col md="6">
                                                            <Label className="small fw-semibold">Refund Amount ({viewingTransaction.currency})</Label>
                                                            <Input type="number" placeholder="Enter refund amount" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
                                                        </Col>
                                                        <Col md="6">
                                                            <Label className="small fw-semibold">Reason</Label>
                                                            <Input type="text" placeholder="Reason for refund" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} />
                                                        </Col>
                                                    </Row>
                                                    <Button color="success" size="sm" onClick={handleRefundSubmit}>Confirm Refund</Button>
                                                </div>
                                            )}

                                            {showDisputeForm && (
                                                <div className="border rounded p-3 bg-white mb-2 animate__animated animate__fadeIn">
                                                    <h6 className="fw-bold text-danger mb-3">Initiate Chargeback Dispute</h6>
                                                    <div className="mb-2">
                                                        <Label className="small fw-semibold">Dispute Claim Reason</Label>
                                                        <Input type="text" placeholder="e.g. fraudulent, unrecognized_charge" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} />
                                                    </div>
                                                    <Button color="danger" size="sm" onClick={handleDisputeSubmit}>Submit Dispute Claim</Button>
                                                </div>
                                            )}
                                        </div>
                                    </Col>

                                    {/* Right Column: Timeline & Event Audits */}
                                    <Col lg="5">
                                        <div className="border rounded p-3 mb-4 bg-light">
                                            <h6 className="fw-bold mb-3 text-dark"><i className="bx bx-git-commit me-1" /> Lifecycle Audit Timeline</h6>
                                            <div className="ms-2 mt-2">
                                                {viewingTransaction.audit_trail?.length > 0 ? (
                                                    viewingTransaction.audit_trail.map((step, index) => (
                                                        <div key={index} className="d-flex gap-3 pb-3 position-relative" style={{ minHeight: "60px" }}>
                                                            {index < viewingTransaction.audit_trail.length - 1 && (
                                                                <div className="position-absolute" style={{ left: "9px", top: "20px", bottom: "0", width: "2px", background: "#cbd5e1" }} />
                                                            )}
                                                            <div className="rounded-circle bg-white border d-flex align-items-center justify-content-center"
                                                                style={{ width: "20px", height: "20px", borderColor: "#64748b", zIndex: 2 }}>
                                                                <div className="rounded-circle" style={{ width: "10px", height: "10px", background: step.status === "success" ? "#10b981" : step.status === "disputed" ? "#ef4444" : "#f59e0b" }} />
                                                            </div>
                                                            <div>
                                                                <span className="small text-muted d-block">{new Date(step.timestamp).toLocaleString()}</span>
                                                                <strong className="text-dark text-capitalize small">{step.event.replace("_", " ")}</strong>
                                                                <p className="text-muted mb-0 small">{step.details}</p>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="d-flex gap-3 pb-3 position-relative">
                                                        <div className="rounded-circle bg-white border d-flex align-items-center justify-content-center" style={{ width: "20px", height: "20px", borderColor: "#64748b" }}>
                                                            <div className="rounded-circle" style={{ width: "10px", height: "10px", background: "#f59e0b" }} />
                                                        </div>
                                                        <div>
                                                            <span className="small text-muted d-block">{viewingTransaction.created_at}</span>
                                                            <strong className="text-dark small">Captured</strong>
                                                            <p className="text-muted mb-0 small">Transaction recorded in dashboard.</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="border rounded p-3 bg-light" style={{ maxHeight: "250px", overflowY: "auto" }}>
                                            <h6 className="fw-bold mb-3 text-dark"><i className="bx bx-terminal me-1" /> Webhook Events Payload History</h6>
                                            {viewingTransaction.webhook_history?.length > 0 ? (
                                                viewingTransaction.webhook_history.map((wh, idx) => (
                                                    <div key={idx} className="p-2 border rounded bg-white mb-2">
                                                        <span className="badge bg-secondary-subtle text-secondary small d-block mb-1 text-start" style={{ textTransform: "none" }}>
                                                            Event: {wh.event}
                                                        </span>
                                                        <span className="small text-muted d-block">Log ID: {wh.id}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <span className="text-muted small">No webhooks event logged.</span>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            )}
                            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Close</button>
                            </div>
                        </div>
                    </Modal>

                </Container>
            </div>
        </React.Fragment>
    );
};

export default Transactions;